export interface Brick {
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  hits: number;
  maxHits: number;
  points: number;
}

export interface Ball {
  x: number;
  y: number;
  dx: number;
  dy: number;
  radius: number;
}

export interface Paddle {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface BrickBreakerState {
  score: number;
  lives: number;
  level: number;
  gameStarted: boolean;
  gameOver: boolean;
  gameWon: boolean;
  isPaused: boolean;
}

export type BrickBreakerAction =
  | { type: 'START' }
  | { type: 'RESTART' }
  | { type: 'TOGGLE_PAUSE' }
  | { type: 'LOSE_LIFE' }
  | { type: 'ADD_SCORE'; points: number }
  | { type: 'NEXT_LEVEL' };

export function createInitialBrickBreakerState(): BrickBreakerState {
  return {
    score: 0,
    lives: 3,
    level: 1,
    gameStarted: false,
    gameOver: false,
    gameWon: false,
    isPaused: false,
  };
}

export function applyBrickBreakerAction(state: BrickBreakerState, action: BrickBreakerAction): BrickBreakerState {
  switch (action.type) {
    case 'START':
      return {
        ...state,
        gameStarted: true,
        gameOver: false,
        gameWon: false,
        isPaused: false,
      };
    case 'RESTART':
      return {
        ...createInitialBrickBreakerState(),
        gameStarted: true,
      };
    case 'TOGGLE_PAUSE':
      return {
        ...state,
        isPaused: !state.isPaused,
      };
    case 'LOSE_LIFE': {
      const nextLives = state.lives - 1;
      if (nextLives <= 0) {
        return {
          ...state,
          lives: 0,
          gameOver: true,
          isPaused: false,
        };
      }
      return {
        ...state,
        lives: nextLives,
      };
    }
    case 'ADD_SCORE':
      return {
        ...state,
        score: state.score + action.points,
      };
    case 'NEXT_LEVEL':
      if (state.level >= 10) {
        return {
          ...state,
          gameWon: true,
          isPaused: false,
        };
      }
      return {
        ...state,
        level: state.level + 1,
        lives: Math.min(state.lives + 1, 5),
      };
    default:
      return state;
  }
}

export function clampPaddleX(x: number, paddleWidth: number, canvasWidth: number): number {
  return Math.max(0, Math.min(canvasWidth - paddleWidth, x));
}

export function stepBallAgainstBounds(ball: Ball, canvasWidth: number, canvasHeight: number): Ball {
  const nextBall: Ball = {
    ...ball,
    x: ball.x + ball.dx,
    y: ball.y + ball.dy,
  };

  if (nextBall.x + nextBall.radius > canvasWidth || nextBall.x - nextBall.radius < 0) {
    nextBall.dx = -nextBall.dx;
  }

  if (nextBall.y - nextBall.radius < 0) {
    nextBall.dy = -nextBall.dy;
  }

  return nextBall;
}

export function resolvePaddleBounce(ball: Ball, paddle: Paddle): Ball {
  if (
    ball.y + ball.radius <= paddle.y ||
    ball.x <= paddle.x ||
    ball.x >= paddle.x + paddle.width
  ) {
    return ball;
  }

  const hitPos = (ball.x - paddle.x) / paddle.width;
  const angle = (hitPos - 0.5) * Math.PI / 3;
  const speed = Math.sqrt(ball.dx * ball.dx + ball.dy * ball.dy);

  return {
    ...ball,
    dx: speed * Math.sin(angle),
    dy: -Math.abs(speed * Math.cos(angle)),
  };
}

export function resolveBrickHit(ball: Ball, bricks: Brick[]): {
  ball: Ball;
  bricks: Brick[];
  scoreIncrease: number;
} {
  const updatedBricks = [...bricks];
  let scoreIncrease = 0;
  let nextBall = ball;

  for (let i = 0; i < updatedBricks.length; i++) {
    const brick = updatedBricks[i];
    if (
      ball.x + ball.radius > brick.x &&
      ball.x - ball.radius < brick.x + brick.width &&
      ball.y + ball.radius > brick.y &&
      ball.y - ball.radius < brick.y + brick.height
    ) {
      const hitBrick = { ...brick, hits: brick.hits + 1 };
      if (hitBrick.hits >= hitBrick.maxHits) {
        scoreIncrease += hitBrick.points;
        updatedBricks.splice(i, 1);
      } else {
        const opacity = 1 - (hitBrick.hits / hitBrick.maxHits) * 0.5;
        hitBrick.color = hitBrick.color.replace(/rgba?\([^)]+\)/, '') + `${Math.floor(opacity * 255).toString(16)}`;
        updatedBricks[i] = hitBrick;
      }

      nextBall = { ...ball, dy: -ball.dy };
      break;
    }
  }

  return { ball: nextBall, bricks: updatedBricks, scoreIncrease };
}